import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  FileText,
  Calendar,
  CalendarDays,
  Eye
} from 'lucide-react';

export const PersonnelList = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [personnel, setPersonnel] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  const fetchData = async () => {
    try {
      const [persRes, catRes] = await Promise.all([
        api.get('/personnel'),
        api.get('/categories')
      ]);
      setPersonnel(persRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Failed to load personnel');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/personnel/${deleteId}`);
      toast.success('Personnel deleted successfully');
      setPersonnel(prev => prev.filter(p => p.id !== deleteId));
    } catch (error) {
      toast.error('Failed to delete personnel');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredPersonnel = personnel.filter(p => {
    const matchesSearch = search === '' || 
      `${p.name} ${p.surname}`.toLowerCase().includes(search.toLowerCase()) ||
      p.national_id.includes(search);
    const matchesCategory = categoryFilter === 'all' || 
      p.category_id === categoryFilter ||
      (categoryFilter === 'unassigned' && !p.category_id);
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Unassigned';
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  const getCategoryColor = (categoryId) => {
    if (!categoryId) return '#6B7280';
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#6B7280';
  };

  if (loading) {
    return (
      <div className="page-transition space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Personnel List</h1>
          <p className="text-muted-foreground">
            Manage all personnel records ({filteredPersonnel.length} total)
          </p>
        </div>
        <Button
          onClick={() => navigate('/personnel/new')}
          className="btn-primary"
          data-testid="add-personnel-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Personnel
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>National ID</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPersonnel.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No personnel found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPersonnel.map((person) => (
                  <TableRow key={person.id} className="table-row-hover" data-testid={`personnel-row-${person.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold text-sm">
                            {person.name.charAt(0)}{person.surname.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{person.name} {person.surname}</p>
                          <p className="text-sm text-muted-foreground">{person.email || 'No email'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{person.national_id}</TableCell>
                    <TableCell>{person.position || '-'}</TableCell>
                    <TableCell>{person.department || '-'}</TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className="gap-1.5"
                        style={{ borderColor: getCategoryColor(person.category_id) }}
                      >
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: getCategoryColor(person.category_id) }}
                        />
                        {getCategoryName(person.category_id)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={person.is_active ? 'default' : 'secondary'}>
                        {person.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`actions-${person.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/personnel/${person.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/documents?personnel=${person.id}`)}>
                            <FileText className="w-4 h-4 mr-2" />
                            Documents
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/timesheet?personnel=${person.id}`)}>
                            <Calendar className="w-4 h-4 mr-2" />
                            Timesheet
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/leaves?personnel=${person.id}`)}>
                            <CalendarDays className="w-4 h-4 mr-2" />
                            Leaves
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteId(person.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Personnel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this personnel? This action cannot be undone
              and will also delete all associated documents, timesheets, and leave records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PersonnelList;
